import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconX, IconMoodSmile, IconAlarm } from "@tabler/icons-react";
import { LocalizationProvider, DateCalendar, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { format } from "date-fns";
import { useBotMessageUpdate } from "hooks";
import { SystemUtils } from "utils";
import { SystemConstant, LangConstant } from "const";
import ConfirmDialog from "./ConfirmDialog";

const EditMessageDialog = ({ message, open, onClose, refetchList }) => {
  const { t: getLabel } = useTranslation();
  const textAreaRef = useRef(null);
  const emojiTheme = (() => {
    const stored = localStorage.getItem("tabler_theme") || "system";
    const resolved = stored === "system"
      ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : stored;
    return resolved === "dark" ? Theme.DARK : Theme.LIGHT;
  })();

  const limitContentLength = 500;

  const { updateBotMessage } = useBotMessageUpdate();

  const [content, setContent] = useState(message?.content);
  const [time, setTime] = useState(message.time && message.time > 0 ? message.time : Date.now());
  const [isOpenEmoji, setOpenEmoji] = useState(false);
  const [isOpenEditTime, setIsOpenEditTime] = useState(false);
  const [isDisableSchedule, setIsDisableSchedule] = useState(true);
  const [isOpenConfirmExit, setIsOpenConfirmExit] = useState(false);
  const [isOpenConfirmDraft, setIsOpenConfirmDraft] = useState(false);
  const [msgError, setMsgError] = useState("");

  const handleChangeForm = event => {
    setContent(event.target.value);
  };

  const handleSendNow = () => {
    if (!Boolean(content)) {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_CONTENT_EMPTY), "error");
    } else {
      updateBotMessage({
        bot_message_id: message.id,
        content: content,
        time: 0,
        status: SystemConstant.BOT_MESSAGE_STATUS.SUCCESS.status,
        onClose: async () => {
          SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_SEND_SUCCESS), "success");
          onClose();
          refetchList();
        },
      });
    }
  };

  const handleDraft = () => {
    if (!Boolean(content)) {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_CONTENT_EMPTY), "error");
    } else {
      if (content.length >= limitContentLength) {
        SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_CONTENT_EXCEEDS), "error");
      } else {
        updateBotMessage({
          bot_message_id: message.id,
          content: content,
          time: 0,
          status: SystemConstant.BOT_MESSAGE_STATUS.DRAFT.status,
          onClose: () => {
            onClose();
            refetchList();
          },
        });
      }
    }
  };

  const handleScheduleSend = () => {
    if (!Boolean(content)) {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_CONTENT_EMPTY), "error");
    } else {
      if (content.length >= limitContentLength) {
        SystemUtils.appendNotification(getLabel(LangConstant.TXT_MEDIA_CONTENT_EXCEEDS), "error");
      } else {
        updateBotMessage({
          bot_message_id: message.id,
          content: content,
          time: time,
          status: SystemConstant.BOT_MESSAGE_STATUS.PENDING.status,
          onClose: () => {
            onClose();
            refetchList();
          },
        });
      }
    }
  };

  const onEmojiClick = emojiObject => {
    const cursorPosition = textAreaRef.current.selectionStart;
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);
    setContent(textBeforeCursor + emojiObject.emoji + textAfterCursor);
  };

  const handleExit = () => {
    if (content && content.length > 0) {
      setIsOpenConfirmExit(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    setIsDisableSchedule(!Boolean(content));

    if (content && content.length >= limitContentLength) {
      setMsgError(getLabel(LangConstant.TXT_MEDIA_MAX_CHARS_ERROR));
    } else {
      setMsgError("");
    }
  }, [content, time, isOpenEditTime]);

  useEffect(() => {
    setContent(message?.content);
    setTime(message.time && message.time > 0 ? message.time : Date.now());
    setOpenEmoji(false);
    setIsOpenEditTime(false);
    setIsOpenConfirmExit(false);
    setIsOpenConfirmDraft(false);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-sm modal-dialog-centered" style={{ maxWidth: "600px" }}>
          <div className="modal-content">
            <ConfirmDialog
              open={isOpenConfirmExit}
              onClose={onClose}
              onSubmit={handleDraft}
              title={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_TITLE)}
              content={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_CONTENT)}
              submit={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_BTN)}
              exit={getLabel(LangConstant.TXT_MEDIA_EXIT)}
            />

            <ConfirmDialog
              open={isOpenConfirmDraft}
              onClose={() => {
                setIsOpenConfirmDraft(false);
              }}
              onSubmit={handleDraft}
              title={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_TITLE)}
              content={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_CONTENT)}
              submit={getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT_BTN)}
              exit={getLabel(LangConstant.L_CANCEL)}
            />

            <div style={{ display: "flex", justifyContent: "space-between", height: "40px", backgroundColor: "var(--tblr-bg-surface-secondary)", alignItems: "center", padding: "0 8px" }}>
              <h5 style={{ fontWeight: "bold", width: "88%", margin: 0, textAlign: "center" }}>
                {isOpenEditTime ? getLabel(LangConstant.TXT_MEDIA_SCHEDULE_SEND) : getLabel(LangConstant.TXT_MEDIA_COMPOSE)}
              </h5>
              <button className="btn btn-icon btn-ghost-secondary" onClick={handleExit}>
                <IconX size={20} />
              </button>
            </div>

            {isOpenEditTime ? (
              <div style={{ padding: "16px" }}>
                <div style={{ display: "flex" }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateCalendar
                      minDate={new Date(Date.now())}
                      value={new Date(time)}
                      onChange={e => {
                        setTime(e.getTime());
                      }}
                    />
                  </LocalizationProvider>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <input
                      className="form-control"
                      style={{ marginTop: "44px" }}
                      disabled
                      value={format(new Date(time), "dd/MM/yyyy")}
                      readOnly
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        sx={{ paddingTop: "24px" }}
                        value={new Date(time)}
                        onChange={e => {
                          if (!isNaN(e) && e) {
                            setTime(e.getTime());
                          } else {
                            setIsDisableSchedule(true);
                          }
                        }}
                      />
                    </LocalizationProvider>

                    <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "44%" }}>
                      <button
                        className="btn"
                        style={{
                          height: "36.5px",
                          backgroundColor: "var(--tblr-bg-surface-secondary)",
                          borderRadius: "8px",
                          fontWeight: "bold",
                        }}
                        onClick={() => setIsOpenEditTime(false)}
                      >
                        {getLabel(LangConstant.L_CANCEL)}
                      </button>
                      <button
                        className="btn btn-primary"
                        disabled={isDisableSchedule}
                        onClick={handleScheduleSend}
                      >
                        {getLabel(LangConstant.TXT_MEDIA_SCHEDULE_BTN)}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: "16px", paddingTop: "6px" }}>
                <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
                  <textarea
                    className={`form-control ${content && content.length >= limitContentLength ? "is-invalid" : ""}`}
                    rows="20"
                    onChange={handleChangeForm}
                    onClick={() => setOpenEmoji(false)}
                    value={content}
                    ref={textAreaRef}
                    placeholder={getLabel(LangConstant.TXT_MEDIA_INPUT_PLACEHOLDER)}
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontSize: "1rem",
                      fontWeight: "400",
                      lineHeight: "1.5",
                    }}
                  />

                  <p style={{ color: "red", margin: 0 }}>{msgError}</p>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                    <div>
                      <IconMoodSmile style={{ cursor: "pointer" }} onClick={() => setOpenEmoji(!isOpenEmoji)} />
                      {isOpenEmoji && (
                        <div style={{ position: "fixed", bottom: "10%", zIndex: 1 }}>
                          <EmojiPicker emojiStyle="apple" emojiVersion="3.0" onEmojiClick={onEmojiClick} theme={emojiTheme} />
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setIsOpenConfirmDraft(true);
                        }}
                        disabled={!Boolean(content)}
                        style={{
                          height: "36.5px",
                          width: "120px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                        }}
                      >
                        {getLabel(LangConstant.TXT_MEDIA_SAVE_DRAFT)}
                      </button>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" }}>
                        <button
                          className="btn btn-primary"
                          onClick={handleSendNow}
                          disabled={!Boolean(content) || msgError.length > 0}
                          style={{ borderRadius: "12px 0 0 12px" }}
                        >
                          {getLabel(LangConstant.TXT_MEDIA_SEND)}
                        </button>

                        <div
                          style={{
                            borderRadius: "0 12px 12px 0",
                            backgroundColor: "#1976d2",
                            color: "white",
                            height: "36.5px",
                            width: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => setIsOpenEditTime(true)}
                        >
                          <IconAlarm size={20} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditMessageDialog;
